import { injectable, inject } from 'inversify';
import { DI_TYPES, DI_CONSTANTS } from './di-types';
import { ClientEvent } from './client-events';
import { constants } from './constants';
import { PersistenceBackend } from './persistence';

export const STORAGE_KEY = `${constants.STORAGE_KEY_PREFIX}:events`;

const CMP_ASC = (a: number, b: number) => a - b;
const CMP_DESC = (a: number, b: number) => b - a;

export type EventWithoutTimestamp = Omit<ClientEvent, 'timestamp'>;

type EventStoreState = {
  size: number;
  events: { [key: string]: EventWithoutTimestamp[] };
};

const EMPTY_STORAGE_STATE: EventStoreState = { size: 0, events: {} };

@injectable()
export class BoundedEventStore {
  private persistenceBackend: PersistenceBackend;
  private maxEventCount: number;
  private _cachedEventStore: EventStoreState | null = null;

  private load() {
    if (!this._cachedEventStore) {
      this._cachedEventStore = JSON.parse(
        this.persistenceBackend.getItem(STORAGE_KEY) || JSON.stringify(EMPTY_STORAGE_STATE)
      ) as EventStoreState;
    }
    return this._cachedEventStore;
  }

  private store(eventStoreState: EventStoreState) {
    this._cachedEventStore = eventStoreState;
    this.persistenceBackend.setItem(STORAGE_KEY, JSON.stringify(eventStoreState));
  }

  constructor(
    @inject(DI_TYPES.PersistenceBackend) persistenceBackend: PersistenceBackend,
    @inject(DI_CONSTANTS.EVENTSTORE_DEFAULT_MAX_EVENT_COUNT) maxEventCount: number
  ) {
    this.persistenceBackend = persistenceBackend;
    this.maxEventCount = maxEventCount;
  }

  static sortEventTimestamps(eventStore: EventStoreState, reverse?: boolean): string[] {
    return Object.keys(eventStore.events)
      .map(ts => parseInt(ts, 10))
      .sort(reverse ? CMP_DESC : CMP_ASC)
      .map(ts => ts + '');
  }

  static appendEvent(eventStore: EventStoreState, event: ClientEvent): EventStoreState {
    const { timestamp, ...eventWithoutTimestamp } = event;
    const key = timestamp + '';
    eventStore.events[key] = (eventStore.events[key] || []).concat(eventWithoutTimestamp);
    eventStore.size += 1;
    return eventStore;
  }

  evictIfNecessary(eventStore: EventStoreState): EventStoreState {
    if (eventStore.size <= this.maxEventCount) {
      return eventStore;
    }

    const reverseSortedKeys = BoundedEventStore.sortEventTimestamps(eventStore);
    // remove events belonging to timestamps, oldest first
    // all events for a timestamp are deleted, even if this is not necessary to appease maxEventCount
    for (let i = 0; eventStore.size > this.maxEventCount && i < reverseSortedKeys.length; i++) {
      const ts = reverseSortedKeys[i] + '';
      eventStore.size -= eventStore.events[ts].length;
      delete eventStore.events[ts];
    }

    return eventStore;
  }

  add(...events: ClientEvent[]) {
    let eventStore = this.load();
    if (events.length > 0) {
      for (let event of events) {
        BoundedEventStore.appendEvent(eventStore, event);
      }
      eventStore = this.evictIfNecessary(eventStore);
      this.store(eventStore);
    }

    return eventStore.size;
  }

  getEvents({ limit = Number.MAX_SAFE_INTEGER }: Partial<{ limit: number }> = {}) {
    return new EventStoreIterator(this.load(), limit);
  }

  hasEventsForTimestamp(timestamp: number) {
    return !!this.load().events[timestamp + ''];
  }
}

export class EventStoreIterator implements Iterable<ClientEvent> {
  private eventStore: EventStoreState;
  private limit: number;
  private eventsReturned: number;
  private currentTimestampIx: number;
  private currentArrayIx: number;
  private reverseSortedKeys: string[];

  private getCurrentTimestamp() {
    return this.reverseSortedKeys[this.currentTimestampIx];
  }

  private getCurrentArray(timestamp = this.getCurrentTimestamp()) {
    return this.eventStore.events[timestamp];
  }

  private getCurrentEvent(): ClientEvent | undefined {
    const timestamp = this.getCurrentTimestamp();
    const array = this.getCurrentArray(timestamp);
    return !array
      ? undefined
      : ({
          timestamp: parseInt(timestamp, 10),
          ...array[array.length - (this.currentArrayIx + 1)],
        } as ClientEvent);
  }

  constructor(eventStore: EventStoreState, limit = Number.MAX_SAFE_INTEGER) {
    this.eventsReturned = 0;
    this.eventStore = eventStore;
    this.limit = limit;
    this.reverseSortedKeys = BoundedEventStore.sortEventTimestamps(eventStore, true);
    this.currentTimestampIx = 0;
    this.currentArrayIx = 0;
  }

  public advance() {
    // attempt to increment currentArrayIx
    const array = this.getCurrentArray();
    if (!array) {
      // no more events, the timestamp index has moved past the last valid timestamp
      return;
    }
    if (this.currentArrayIx >= array.length - 1) {
      // current array exhausted, time to move on to the next timestamp
      this.currentArrayIx = 0;
      this.currentTimestampIx += 1;
      return;
    }
    this.currentArrayIx += 1;
  }

  public getNextEvent(): ClientEvent | undefined {
    const event = this.getCurrentEvent();
    if (event) {
      this.eventsReturned += 1;
      this.advance();
    }
    return event;
  }

  public [Symbol.iterator](): Iterator<ClientEvent> {
    return {
      next: (): IteratorResult<ClientEvent> => {
        const value = this.eventsReturned >= this.limit ? undefined : this.getNextEvent();
        if (value) {
          return { value };
        } else {
          return { done: true, value: this.eventsReturned };
        }
      },
    };
  }
}
